import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Bell, BellOff, CheckCircle, AlertCircle, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

// Convert VAPID key from base64 to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

const NotificationSettings = () => {
  const [isSupported, setIsSupported] = useState(false);
  const [permission, setPermission] = useState('default');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    checkNotificationSupport();
  }, []);

  const checkNotificationSupport = async () => {
    // Check if notifications are supported
    const supported = 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;
    setIsSupported(supported);
    
    if (supported) {
      setPermission(Notification.permission);
      
      // Check if already subscribed
      try {
        const response = await axios.get(`${API}/push/status`);
        setIsSubscribed(response.data.subscribed);
      } catch (error) {
        console.error('Error checking subscription status:', error);
      }
    }
    setLoading(false);
  };

  const subscribeToNotifications = async () => {
    setLoading(true);
    try {
      // Request notification permission
      const permissionResult = await Notification.requestPermission();
      setPermission(permissionResult);
      
      if (permissionResult !== 'granted') {
        toast.error('Notification permission denied');
        setLoading(false);
        return;
      }

      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;
      
      // Subscribe to push notifications
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
      });

      // Send subscription to server
      const subscriptionJson = subscription.toJSON();
      await axios.post(`${API}/push/subscribe`, {
        endpoint: subscriptionJson.endpoint,
        keys: subscriptionJson.keys
      });

      setIsSubscribed(true);
      toast.success('Push notifications enabled!');
    } catch (error) {
      console.error('Error subscribing to notifications:', error);
      toast.error('Failed to enable notifications. Please try again.');
    }
    setLoading(false);
  };

  const unsubscribeFromNotifications = async () => {
    setLoading(true);
    try {
      // Get current subscription and unsubscribe
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        await subscription.unsubscribe();
      }

      // Tell server to remove subscription
      await axios.post(`${API}/push/unsubscribe`);
      
      setIsSubscribed(false);
      toast.success('Push notifications disabled');
    } catch (error) {
      console.error('Error unsubscribing:', error);
      toast.error('Failed to disable notifications');
    }
    setLoading(false);
  };

  const testNotification = async () => {
    setTesting(true);
    try {
      await axios.post(`${API}/push/test`);
      toast.success('Test notification sent! Check your device.');
    } catch (error) {
      console.error('Error sending test notification:', error);
      toast.error(error.response?.data?.detail || 'Failed to send test notification');
    }
    setTesting(false);
  };

  if (!isSupported) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="flex items-center gap-2 text-amber-700">
          <AlertCircle size={20} />
          <span>Push notifications are not supported in this browser.</span>
        </div>
        <p className="text-sm text-amber-600 mt-2">
          Try using Chrome, Firefox, or Edge on desktop/Android, or Safari on iOS 16.4+
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-6">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-blue-100 rounded-lg">
          <Bell className="text-blue-600" size={24} />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Push Notifications</h3>
          <p className="text-sm text-slate-500">Get notified about important updates</p>
        </div>
      </div>

      {/* Permission Status */}
      {permission === 'denied' && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-red-700">
            <BellOff size={20} />
            <span className="font-medium">Notifications Blocked</span>
          </div>
          <p className="text-sm text-red-600 mt-2">
            You've blocked notifications for this site. To enable them:
          </p>
          <ol className="text-sm text-red-600 mt-2 list-decimal ml-5 space-y-1">
            <li>Click the lock/info icon in your browser's address bar</li>
            <li>Find "Notifications" and change to "Allow"</li>
            <li>Refresh the page</li>
          </ol>
        </div>
      )}

      {/* Toggle and Test */}
      {permission !== 'denied' && (
        <>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Switch
                id="notifications"
                checked={isSubscribed}
                onCheckedChange={(checked) => {
                  if (checked) {
                    subscribeToNotifications();
                  } else {
                    unsubscribeFromNotifications();
                  }
                }}
                disabled={loading}
              />
              <Label htmlFor="notifications" className="cursor-pointer">
                {isSubscribed ? 'Notifications enabled' : 'Enable notifications'}
              </Label>
            </div>
            {isSubscribed && (
              <CheckCircle className="text-emerald-500" size={20} />
            )}
          </div>

          {isSubscribed && (
            <div className="pt-4 border-t">
              <Button
                variant="outline"
                onClick={testNotification}
                disabled={testing}
                className="w-full"
              >
                {testing ? 'Sending...' : '🔔 Send Test Notification'}
              </Button>
              <p className="text-xs text-slate-500 mt-2 text-center">
                You'll receive notifications for:
              </p>
              <ul className="text-xs text-slate-500 mt-1 space-y-1">
                <li>✓ Timesheet approvals</li>
                <li>✓ Cash advance updates</li>
                <li>✓ Payslip creation</li>
              </ul>
            </div>
          )}
        </>
      )}

      {/* PWA Tip */}
      <div className="bg-slate-50 rounded-lg p-4">
        <div className="flex items-center gap-2 text-slate-700 mb-2">
          <Smartphone size={18} />
          <span className="font-medium text-sm">Pro Tip</span>
        </div>
        <p className="text-xs text-slate-600">
          Install Mipay as an app on your phone for the best notification experience! 
          Look for "Add to Home Screen" in your browser menu.
        </p>
      </div>
    </div>
  );
};

export default NotificationSettings;
